import { useState } from "react";
import api from "../api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Instagram, Youtube, Send, Mail, Phone, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";

const TYPES = ["press", "booking", "partnership", "general"];

const Contact = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    city: "",
    inquiry_type: "booking",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      toast.success(t.contact.success);
      setForm({
        name: "",
        email: "",
        organization: "",
        city: "",
        inquiry_type: "booking",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setSubscribing(true);
    try {
      await api.post("/newsletter", { email: newsletterEmail });
      toast.success(t.newsletter.success);
      setNewsletterEmail("");
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-10 lg:gap-20">
          <div className="col-span-12 lg:col-span-5">
            <div className="kicker mb-4">{t.contact.kicker}</div>
            <h2
              className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter"
              data-testid="contact-title"
            >
              {t.contact.title}
            </h2>
            <p className="mt-6 text-white/65 max-w-md leading-relaxed">
              {t.contact.subtitle}
            </p>

            <div className="mt-10 space-y-4">
              <a
                href="mailto:info@apexevent.net"
                className="inline-flex items-center gap-3 text-white/85 hover:text-[#FFDE00] transition-colors"
                data-testid="contact-email"
              >
                <Mail size={18} /> info@apexevent.net
              </a>
              <a
                href="tel:+77073001064"
                className="inline-flex items-center gap-3 text-white/85 hover:text-[#FFDE00] transition-colors"
                data-testid="contact-phone"
              >
                <Phone size={18} /> +7 (707) 300 1064
              </a>
              <a
                href="https://instagram.com/team_astana"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-white/85 hover:text-[#FFDE00] transition-colors"
                data-testid="contact-instagram-link"
              >
                <Instagram size={18} /> @team_astana
              </a>
              <div className="flex items-center gap-3 pt-2">
                <a
                  href="https://instagram.com/team_astana"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#FFDE00] hover:text-black hover:border-[#FFDE00] transition-all"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                >
                  <Instagram size={16} />
                </a>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#FFDE00] hover:text-black hover:border-[#FFDE00] transition-all"
                  data-testid="social-youtube"
                  aria-label="YouTube"
                >
                  <Youtube size={16} />
                </a>
                <a
                  href="https://t.me/team_astana"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#FFDE00] hover:text-black hover:border-[#FFDE00] transition-all"
                  data-testid="social-telegram"
                  aria-label="Telegram"
                >
                  <Send size={16} />
                </a>
              </div>
            </div>

            {/* Newsletter */}
            <div className="mt-14 border-t border-white/10 pt-10">
              <div className="font-display text-2xl tracking-tight">{t.newsletter.title}</div>
              <p className="text-white/55 text-sm mt-2 max-w-sm">{t.newsletter.subtitle}</p>
              <form onSubmit={subscribe} className="mt-5 flex items-end gap-3" data-testid="newsletter-form">
                <input
                  type="email"
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder={t.newsletter.placeholder}
                  className="field-input flex-1"
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={subscribing}
                  data-testid="newsletter-submit"
                >
                  {subscribing ? <Loader2 size={16} className="animate-spin" /> : t.newsletter.cta}
                </button>
              </form>
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            onSubmit={submit}
            className="col-span-12 lg:col-span-7 space-y-7"
            data-testid="booking-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
              <div>
                <label className="text-[10px] tracking-widest uppercase text-white/45">
                  {t.contact.name}
                </label>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="field-input"
                  data-testid="form-name"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-white/45">
                  {t.contact.email}
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="field-input"
                  data-testid="form-email"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-white/45">
                  {t.contact.organization}
                </label>
                <input
                  value={form.organization}
                  onChange={update("organization")}
                  className="field-input"
                  data-testid="form-org"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-white/45">
                  {t.contact.city}
                </label>
                <input
                  value={form.city}
                  onChange={update("city")}
                  className="field-input"
                  data-testid="form-city"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] tracking-widest uppercase text-white/45 block mb-3">
                {t.contact.type}
              </label>
              <div className="flex flex-wrap gap-2" data-testid="form-type-group">
                {TYPES.map((tp) => (
                  <button
                    type="button"
                    key={tp}
                    onClick={() => setForm((p) => ({ ...p, inquiry_type: tp }))}
                    data-testid={`form-type-${tp}`}
                    className={`px-4 py-2 text-xs tracking-widest uppercase font-semibold border transition-all ${
                      form.inquiry_type === tp
                        ? "bg-[#FFDE00] text-black border-[#FFDE00]"
                        : "text-white/70 border-white/15 hover:text-white hover:border-white/40"
                    }`}
                  >
                    {t.contact.types[tp]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] tracking-widest uppercase text-white/45">
                {t.contact.message}
              </label>
              <textarea
                required
                value={form.message}
                onChange={update("message")}
                rows={5}
                className="field-input"
                data-testid="form-message"
              />
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
              data-testid="form-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> {t.contact.sending}
                </>
              ) : (
                <>
                  {t.contact.submit} <Send size={14} />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
